/**
 * Persist composed-task upload group routing across full page reloads.
 * In-memory refs in GlobalProvider are empty after refresh; without this,
 * per-clone WebSocket `updateProgress` would create duplicate FloatingProgressBar rows.
 */

export const COMPOSE_UPLOAD_GROUPS_STORAGE_KEY = 'specificai:composeUploadGroups:v1';

/**
 * @typedef {Object} ComposeUploadGroupMetadata
 * @property {string} [datasetDisplayName]
 * @property {string[]} [affectedUsecaseIds]
 * @property {string} [clientId]
 * @property {boolean} [isBenchmark]
 * @property {string} [usecaseIdPrimary]
 */

/**
 * @returns {Record<string, { children: string[], metadata?: ComposeUploadGroupMetadata }>}
 */
export function loadComposeUploadGroups() {
    if (typeof sessionStorage === 'undefined') return {};
    try {
        const raw = sessionStorage.getItem(COMPOSE_UPLOAD_GROUPS_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (
            !parsed ||
            typeof parsed !== 'object' ||
            parsed.version !== 1 ||
            typeof parsed.groups !== 'object'
        ) {
            return {};
        }
        return parsed.groups;
    } catch {
        return {};
    }
}

/**
 * @param {Record<string, { children: string[], metadata?: ComposeUploadGroupMetadata }>} groups
 */
export function saveComposeUploadGroups(groups) {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(
            COMPOSE_UPLOAD_GROUPS_STORAGE_KEY,
            JSON.stringify({ version: 1, groups: groups || {} })
        );
    } catch {
        // Quota / private mode — coalescing survives until reload only.
    }
}

/**
 * @param {string} parentKey
 * @param {string[]} children
 * @param {ComposeUploadGroupMetadata} [metadata]
 */
export function upsertComposeUploadGroup(parentKey, children, metadata = {}) {
    if (!parentKey || !Array.isArray(children) || children.length === 0) return;
    const groups = loadComposeUploadGroups();
    groups[parentKey] = {
        children: children.filter(Boolean),
        metadata: metadata && typeof metadata === 'object' ? { ...metadata } : {},
    };
    saveComposeUploadGroups(groups);
}

/** Remove one parent group from session (refs cleared separately in GlobalProvider). */
export function removeComposeUploadGroup(parentKey) {
    if (!parentKey) return;
    const groups = loadComposeUploadGroups();
    if (!groups[parentKey]) return;
    delete groups[parentKey];
    saveComposeUploadGroups(groups);
}
